import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  ListSubheader
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const API_ENDPOINTS = [
  // Auth endpoints
  { method: 'GET', path: '/api/auth/me', name: 'Get Current User', requiresAuth: true, category: 'Auth' },
  { method: 'GET', path: '/api/public/stats', name: 'Public Stats', requiresAuth: false, category: 'Public' },
  
  // Skis endpoints
  { method: 'GET', path: '/api/skis', name: 'Get My Skis', requiresAuth: true, category: 'Skis' },
  
  // Tests endpoints
  { method: 'GET', path: '/api/tests', name: 'Get My Tests', requiresAuth: true, category: 'Tests' },
  
  // Races endpoints
  { method: 'GET', path: '/api/races', name: 'Get My Races', requiresAuth: true, category: 'Races' },
  
  // Teams endpoints
  { method: 'GET', path: '/api/teams', name: 'Get My Teams', requiresAuth: true, category: 'Teams' },
  
  // Admin endpoints
  { method: 'GET', path: '/api/admin/stats', name: 'Admin Statistics', requiresAuth: true, category: 'Admin' },
  { method: 'GET', path: '/api/admin/users', name: 'Get Users', requiresAuth: true, category: 'Admin' },
  { method: 'GET', path: '/api/admin/teams', name: 'Get Teams', requiresAuth: true, category: 'Admin' },
  { method: 'GET', path: '/api/admin/bugs', name: 'Get Bug Reports', requiresAuth: true, category: 'Admin' },
  
  // Waxing Diary endpoints
  { method: 'GET', path: '/api/waxing-diary/my', name: 'Get My Waxing Diary', requiresAuth: true, category: 'Waxing' },
  
  // Wax Tests endpoints
  { method: 'GET', path: '/api/wax-tests/my', name: 'Get My Wax Tests', requiresAuth: true, category: 'Wax Tests' },
  
  // Coach Ski Fleet endpoints
  { method: 'GET', path: '/api/coach-ski-fleet', name: 'Get Coach Ski Fleet', requiresAuth: true, category: 'Coach Fleet' },
  
  // Events endpoints
  { method: 'GET', path: '/api/events', name: 'Get Events', requiresAuth: true, category: 'Events' },
  
  // Ski Requests endpoints
  { method: 'GET', path: '/api/ski-requests', name: 'Get Ski Requests', requiresAuth: true, category: 'Ski Requests' },
];

const API_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:5003/api');

export default function ApiTester() {
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [requestBody, setRequestBody] = useState('{}');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const theme = useTheme();

  const [token, setToken] = useState(localStorage.getItem('token'));
  const [autoLoginStatus, setAutoLoginStatus] = useState('');

  // Auto-login with test credentials if needed
  const ensureAuth = async () => {
    if (token) return token;
    
    try {
      setAutoLoginStatus('Logging in with test credentials...');
      const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:5003/api'
        : '/api';
      
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'athlete1@test.com', password: 'password123' })
      });
      
      if (!res.ok) {
        throw new Error('Failed to login with test credentials');
      }
      
      const data = await res.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setToken(data.token);
      setAutoLoginStatus('✓ Logged in with test credentials');
      // Clear status after 3 seconds
      setTimeout(() => setAutoLoginStatus(''), 3000);
      return data.token;
    } catch (err) {
      setAutoLoginStatus('✗ Failed to login: ' + err.message);
      throw err;
    }
  };

  const handleTest = async () => {
    console.log('handleTest called', { selectedEndpoint });
    
    if (!selectedEndpoint) {
      setError('Please select an endpoint');
      return;
    }

    const endpoint = API_ENDPOINTS.find(e => e.path === selectedEndpoint);
    if (!endpoint) {
      setError('Invalid endpoint selected');
      return;
    }

    // Auto-login if auth is required
    let authToken = token;
    if (endpoint.requiresAuth && !authToken) {
      try {
        authToken = await ensureAuth();
      } catch (err) {
        setError('Failed to authenticate with test credentials: ' + err.message);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setResponse(null);
    
    console.log('Starting API test:', { endpoint: endpoint.name, url: selectedEndpoint });

    const startTime = Date.now();
    const testResult = {
      endpoint: endpoint.name,
      method: endpoint.method,
      path: endpoint.path,
      timestamp: new Date().toLocaleString(),
      status: null,
      duration: null,
      success: false
    };

    try {
      let body = null;
      if (requestBody && requestBody.trim() !== '') {
        try {
          body = JSON.parse(requestBody);
        } catch (e) {
          throw new Error('Invalid JSON in request body');
        }
      }

      const headers = {
        'Content-Type': 'application/json',
      };

      if (endpoint.requiresAuth && authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Construct full URL - endpoints include /api prefix
      let url;
      if (selectedEndpoint.startsWith('http')) {
        url = selectedEndpoint;
      } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        // In development, use full URL with localhost:5003
        url = selectedEndpoint.startsWith('/api') 
          ? `http://localhost:5003${selectedEndpoint}` 
          : `${API_URL}${selectedEndpoint}`;
      } else {
        // In production, use relative path
        url = selectedEndpoint;
      }
      const options = {
        method: endpoint.method,
        headers,
      };

      if (body && (endpoint.method === 'POST' || endpoint.method === 'PUT' || endpoint.method === 'PATCH')) {
        options.body = JSON.stringify(body);
      }

      console.log('Fetching URL:', url, 'with options:', options);
      const res = await fetch(url, options);
      const duration = Date.now() - startTime;
      
      console.log('Response received:', { status: res.status, statusText: res.statusText });
      
      let data;
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        data = await res.text();
      }
      
      console.log('Response data:', data);

      testResult.status = res.status;
      testResult.duration = `${duration}ms`;
      testResult.success = res.ok;
      testResult.response = data;

      setResponse({
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        data: data,
        duration: `${duration}ms`
      });

      setTestHistory(prev => [testResult, ...prev.slice(0, 9)]); // Keep last 10 tests
    } catch (err) {
      const duration = Date.now() - startTime;
      console.error('Error testing endpoint:', err);
      testResult.status = 'Error';
      testResult.duration = `${duration}ms`;
      testResult.success = false;
      testResult.error = err.message;

      setError(`Error: ${err.message}. Check console for details.`);
      setTestHistory(prev => [testResult, ...prev.slice(0, 9)]);
    } finally {
      setLoading(false);
      console.log('Test completed');
    }
  };

  const categories = [...new Set(API_ENDPOINTS.map(e => e.category))];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>API Endpoint Tester</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        This tool tests API endpoints without creating database records. Most endpoints are GET requests that only read data.
        Use this to verify your API is working correctly.
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Select Endpoint</InputLabel>
            <Select
              value={selectedEndpoint}
              onChange={(e) => {
                console.log('Select onChange:', e.target.value);
                setSelectedEndpoint(e.target.value);
                setResponse(null);
                setError(null);
              }}
              label="Select Endpoint"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
            >
              {categories.map(category => [
                <ListSubheader 
                  key={`header-${category}`} 
                  sx={{ 
                    fontWeight: 'bold', 
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                    color: theme.palette.text.primary,
                    lineHeight: '2'
                  }}
                >
                  {category}
                </ListSubheader>,
                ...API_ENDPOINTS
                  .filter(e => e.category === category)
                  .map(endpoint => (
                    <MenuItem 
                      key={endpoint.path} 
                      value={endpoint.path}
                      sx={{
                        '&:hover': {
                          bgcolor: theme.palette.action.hover
                        }
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip 
                          label={endpoint.method} 
                          size="small" 
                          color={endpoint.method === 'GET' ? 'primary' : 'secondary'}
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography sx={{ color: theme.palette.text.primary }}>{endpoint.name}</Typography>
                      </Stack>
                    </MenuItem>
                  ))
              ])}
            </Select>
          </FormControl>

          {autoLoginStatus && (
            <Alert severity={autoLoginStatus.startsWith('✓') ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {autoLoginStatus}
            </Alert>
          )}

          {selectedEndpoint && (
            <>
              <Alert severity="info" sx={{ mb: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                Selected: <strong>{API_ENDPOINTS.find(e => e.path === selectedEndpoint)?.name}</strong> ({API_ENDPOINTS.find(e => e.path === selectedEndpoint)?.method})
                {API_ENDPOINTS.find(e => e.path === selectedEndpoint)?.requiresAuth && !token && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Will auto-login with test credentials
                  </Typography>
                )}
              </Alert>
              
              <TextField
                label="Request Body (JSON)"
                multiline
                rows={4}
                value={requestBody}
                onChange={(e) => setRequestBody(e.target.value)}
                fullWidth
                helperText="Only used for POST, PUT, PATCH requests"
                disabled={API_ENDPOINTS.find(e => e.path === selectedEndpoint)?.method === 'GET'}
              />

              <Button
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  console.log('Button clicked, calling handleTest');
                  handleTest();
                }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                fullWidth
                size="large"
                type="button"
              >
                {loading ? 'Testing...' : 'Test Endpoint'}
              </Button>
            </>
          )}

          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          {response && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Chip
                    icon={response.status < 400 ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={`Status: ${response.status} ${response.statusText}`}
                    color={response.status < 400 ? 'success' : 'error'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Duration: {response.duration}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Response Data:</Typography>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'background.paper', 
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'auto', 
                        maxHeight: 400 
                      }}
                    >
                      <pre style={{ 
                        margin: 0, 
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {response.data && typeof response.data === 'object' 
                          ? JSON.stringify(response.data, null, 2)
                          : String(response.data || '(empty response)')}
                      </pre>
                    </Paper>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Response Headers:</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Header</strong></TableCell>
                            <TableCell><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(response.headers).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell>{key}</TableCell>
                              <TableCell>{String(value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </Paper>

      {testHistory.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Test History</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testHistory.map((test, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{test.timestamp}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {test.endpoint}
                    </TableCell>
                    <TableCell>
                      <Chip label={test.method} size="small" />
                    </TableCell>
                    <TableCell>{test.status}</TableCell>
                    <TableCell>{test.duration}</TableCell>
                    <TableCell>
                      <Chip
                        icon={test.success ? <CheckCircleIcon /> : <ErrorIcon />}
                        label={test.success ? 'Success' : 'Failed'}
                        color={test.success ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

